import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useLocation } from "wouter";

/**
 * Three faces of the platform:
 * - MARKETING: Public homepage, conversion-optimized
 * - APPLICATION: Authenticated dashboard, data-dense
 * - TENANT: Company-specific customization
 */
export type PlatformFace = "marketing" | "application" | "tenant";

interface FaceContextValue {
  currentFace: PlatformFace;
  setFace: (face: PlatformFace) => void;
}

const FaceContext = createContext<FaceContextValue | undefined>(undefined);

/**
 * Map routes to platform faces
 */
function detectFaceFromRoute(pathname: string): PlatformFace {
  // Marketing face - public routes
  if (
    pathname === "/" ||
    pathname === "/pricing" ||
    pathname === "/features" ||
    pathname === "/demo" ||
    pathname === "/about"
  ) {
    return "marketing";
  }

  // Tenant face - company customization routes
  if (
    pathname.startsWith("/company-profile") ||
    pathname.startsWith("/tenant-settings") ||
    pathname.startsWith("/branding")
  ) {
    return "tenant";
  }

  // Application face - all authenticated routes (default)
  return "application";
}

interface FaceProviderProps {
  children: ReactNode;
}

export function FaceProvider({ children }: FaceProviderProps) {
  const [location] = useLocation();
  const [currentFace, setCurrentFace] = useState<PlatformFace>(() =>
    detectFaceFromRoute(location)
  );

  // Update face when route changes
  useEffect(() => {
    const detectedFace = detectFaceFromRoute(location);
    setCurrentFace(detectedFace);

    // Add face class to document root for CSS targeting
    document.documentElement.setAttribute("data-face", detectedFace);

    // Optional: Add face-specific class for styling
    document.documentElement.classList.remove(
      "face-marketing",
      "face-application",
      "face-tenant"
    );
    document.documentElement.classList.add(`face-${detectedFace}`);
  }, [location]);

  const value: FaceContextValue = {
    currentFace,
    setFace: setCurrentFace,
  };

  return <FaceContext.Provider value={value}>{children}</FaceContext.Provider>;
}

export function useFace(): FaceContextValue {
  const context = useContext(FaceContext);
  if (!context) {
    throw new Error("useFace must be used within a FaceProvider");
  }
  return context;
}

/**
 * Hook to check if currently on a specific face
 */
export function useIsFace(face: PlatformFace): boolean {
  const { currentFace } = useFace();
  return currentFace === face;
}

/**
 * Hook to get face-specific value
 */
export function useFaceValue<T>(values: Record<PlatformFace, T>): T {
  const { currentFace } = useFace();
  return values[currentFace];
}
